import { useState, useEffect } from 'react';
import axios from 'axios';
import { Sparkles, Download, FolderPlus } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import theme from '../theme';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NftArtAI({ user }) {
  const [prompt, setPrompt] = useState('');
  const [count, setCount] = useState(1);
  const [seed, setSeed] = useState('');
  const [generating, setGenerating] = useState(false);
  const [images, setImages] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/nft/projects`, { headers: { Authorization: `Bearer ${token}` } });
        setProjects(res.data.projects || []);
      } catch (err) { /* silent */ }
    };
    fetchProjects();
  }, []);

  const handleGenerate = async () => {
    if (!prompt.trim()) { toast.error('Enter a prompt'); return; }
    setGenerating(true);
    try {
      const token = localStorage.getItem('token');
      const body = { prompt: prompt.trim(), count, project_id: selectedProject || undefined };
      if (seed) body.seed = parseInt(seed, 10) || undefined;
      const res = await axios.post(`${API}/nft/images/ai-generate`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const newImages = (res.data.images || []).map((img) => ({
        ...img,
        full_url: `${process.env.REACT_APP_BACKEND_URL}${img.image_url}`,
      }));
      setImages((prev) => [...newImages, ...prev]);
      toast.success(`Generated ${newImages.length} image(s)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div data-testid="nft-art-ai-page">
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ fontFamily: theme.fonts.heading, fontSize: '28px', fontWeight: '700', color: theme.colors.text.primary, marginBottom: '8px' }}>
          AI Image Generator
        </h1>
        <p style={{ color: theme.colors.text.secondary, fontSize: '15px' }}>
          Generate NFT art from text prompts. Images are saved and can be linked to projects.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
        {/* Controls */}
        <Card style={{ border: `1px solid ${theme.colors.borders}`, borderRadius: theme.radius.card }}>
          <CardHeader>
            <CardTitle style={{ color: '#A855F7', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Sparkles size={18} /> Generate
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label style={{ color: theme.colors.text.primary }}>Prompt</Label>
              <textarea
                data-testid="ai-prompt-input"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Describe your NFT art... e.g. 'A cosmic cat in space with neon vibes'"
                rows={4}
                style={{
                  width: '100%', padding: '10px 12px', borderRadius: '8px', fontSize: '14px', resize: 'vertical',
                  background: 'rgba(168, 85, 247, 0.05)', border: '1px solid rgba(168, 85, 247, 0.2)', color: theme.colors.text.primary,
                }}
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <Label style={{ color: theme.colors.text.primary }}>Count (1-10)</Label>
                <Input
                  data-testid="ai-count-input"
                  type="number" min={1} max={10} value={count} onChange={(e) => setCount(Math.min(10, Math.max(1, parseInt(e.target.value) || 1)))}
                  style={{ background: 'rgba(168, 85, 247, 0.05)', borderColor: 'rgba(168, 85, 247, 0.2)', color: theme.colors.text.primary }}
                />
              </div>
              <div>
                <Label style={{ color: theme.colors.text.primary }}>Seed (optional)</Label>
                <Input
                  data-testid="ai-seed-input"
                  type="number" value={seed} onChange={(e) => setSeed(e.target.value)} placeholder="Random"
                  style={{ background: 'rgba(168, 85, 247, 0.05)', borderColor: 'rgba(168, 85, 247, 0.2)', color: theme.colors.text.primary }}
                />
              </div>
            </div>
            {projects.length > 0 && (
              <div>
                <Label style={{ color: theme.colors.text.primary }}>Save to Project (optional)</Label>
                <select
                  data-testid="ai-project-select"
                  value={selectedProject} onChange={(e) => setSelectedProject(e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', fontSize: '14px', background: 'rgba(168, 85, 247, 0.05)', border: '1px solid rgba(168, 85, 247, 0.2)', color: theme.colors.text.primary }}
                >
                  <option value="">None</option>
                  {projects.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.quantity})</option>)}
                </select>
              </div>
            )}
            <Button
              data-testid="ai-generate-btn"
              onClick={handleGenerate}
              disabled={generating || !prompt.trim()}
              style={{ width: '100%', background: 'linear-gradient(135deg, #A855F7, #6366F1)', color: '#FFF', padding: '12px', fontWeight: '600' }}
            >
              {generating ? 'Generating...' : `Generate ${count} Image${count > 1 ? 's' : ''}`}
            </Button>
          </CardContent>
        </Card>

        {/* Results */}
        <Card style={{ border: `1px solid ${theme.colors.borders}`, borderRadius: theme.radius.card }}>
          <CardHeader>
            <CardTitle style={{ color: theme.colors.text.primary }}>Generated Images ({images.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {images.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 16px' }}>
                <Sparkles size={36} style={{ color: theme.colors.text.muted, margin: '0 auto 12px' }} />
                <p style={{ color: theme.colors.text.muted, fontSize: '14px' }}>Generated images will appear here</p>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '12px', maxHeight: '500px', overflowY: 'auto' }}>
                {images.map((img) => (
                  <div
                    key={img.id}
                    data-testid={`ai-image-${img.id}`}
                    style={{ borderRadius: '8px', overflow: 'hidden', border: `1px solid ${theme.colors.borders}`, position: 'relative' }}
                  >
                    <img
                      src={img.full_url}
                      alt={img.prompt}
                      style={{ width: '100%', height: '140px', objectFit: 'cover', display: 'block' }}
                    />
                    <div style={{ padding: '6px 8px', background: 'rgba(0,0,0,0.03)' }}>
                      <p style={{ fontSize: '11px', color: theme.colors.text.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        seed: {img.seed}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
